import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Plus, Loader2, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  getShiftLogEntries,
  addShiftLogEntry,
  updateShiftLogEntry,
  deleteShiftLogEntry,
  type ShiftLogEntry,
} from '@/services/db/shiftLog';
import { useAuthStore } from '@/store/authStore';
import { FieldLabel, formatDateTimeLocal, fromDateTimeLocal } from './_shared';

interface Props {
  dsrId: string;
  isReadOnly: boolean;
}

interface EditState {
  id: string | null;
  entryTime: string;
  entryText: string;
}

export default function ShiftLogForm({ dsrId, isReadOnly }: Props) {
  const user = useAuthStore((s) => s.user);
  const tenantId = useAuthStore((s) => s.tenantId);
  const queryClient = useQueryClient();

  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['shift_log', dsrId],
    queryFn: () => getShiftLogEntries(dsrId),
    staleTime: 30 * 1000,
  });

  const startAdd = () => {
    setError(null);
    setEdit({
      id: null,
      entryTime: formatDateTimeLocal(new Date().toISOString()),
      entryText: '',
    });
  };

  const startEdit = (entry: ShiftLogEntry) => {
    setError(null);
    setEdit({
      id: entry.id,
      entryTime: formatDateTimeLocal(entry.entry_time),
      entryText: entry.entry_text,
    });
  };

  const cancel = () => {
    setEdit(null);
    setError(null);
  };

  const save = async () => {
    if (!edit || !user || !tenantId) return;
    if (!edit.entryText.trim()) {
      setError('Entry text is required.');
      return;
    }
    if (!edit.entryTime) {
      setError('Entry time is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const isoTime = fromDateTimeLocal(edit.entryTime);
      if (edit.id) {
        await updateShiftLogEntry(edit.id, {
          entry_text: edit.entryText.trim(),
          entry_time: isoTime,
        });
      } else {
        await addShiftLogEntry({
          dsrReportId: dsrId,
          tenantId,
          userId: user.id,
          entryTime: isoTime,
          entryText: edit.entryText.trim(),
        });
      }
      queryClient.invalidateQueries({ queryKey: ['shift_log', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
      setEdit(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this log entry?')) return;
    try {
      await deleteShiftLogEntry(id);
      queryClient.invalidateQueries({ queryKey: ['shift_log', dsrId] });
      queryClient.invalidateQueries({ queryKey: ['entry_completion', dsrId] });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-mozaic-teal" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <h2 className="text-base font-semibold">Shift Log</h2>
          <p className="text-xs text-muted-foreground">
            Running log of shift events — add entries as the shift progresses.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {entries.length === 0 && !edit && (
            <p className="rounded-md border border-dashed border-muted bg-muted/30 px-3 py-6 text-center text-sm text-muted-foreground">
              No log entries yet.
              {!isReadOnly && (
                <>
                  <br />
                  <button
                    type="button"
                    onClick={startAdd}
                    className="mt-2 inline-flex items-center gap-1 text-mozaic-teal hover:underline"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add your first entry
                  </button>
                </>
              )}
            </p>
          )}

          {entries.map((entry) => (
            <div
              key={entry.id}
              className="flex items-start gap-3 rounded-md border border-border bg-background px-3 py-2"
            >
              <div className="w-20 shrink-0 text-xs font-semibold text-mozaic-teal">
                {format(new Date(entry.entry_time), 'HH:mm')}
              </div>
              <p className="flex-1 whitespace-pre-wrap text-sm leading-relaxed">
                {entry.entry_text}
              </p>
              {!isReadOnly && entry.created_by === user?.id && (
                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => startEdit(entry)}
                    aria-label="Edit entry"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-mozaic-red"
                    onClick={() => remove(entry.id)}
                    aria-label="Delete entry"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          ))}

          {edit && (
            <div className="space-y-2 rounded-md border-2 border-mozaic-teal/40 bg-mozaic-teal/5 p-3">
              <div className="grid gap-2 sm:grid-cols-[200px_1fr]">
                <div className="space-y-1">
                  <FieldLabel required>Time</FieldLabel>
                  <Input
                    type="datetime-local"
                    value={edit.entryTime}
                    onChange={(e) => setEdit({ ...edit, entryTime: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <FieldLabel required>Entry</FieldLabel>
                  <Textarea
                    rows={4}
                    value={edit.entryText}
                    onChange={(e) => setEdit({ ...edit, entryText: e.target.value })}
                    placeholder="What happened? (e.g. crew change, weather event, equipment issue)"
                  />
                </div>
              </div>
              {error && (
                <p className="text-xs text-mozaic-red">{error}</p>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={cancel} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-mozaic-teal hover:bg-mozaic-teal/90"
                  onClick={save}
                  disabled={saving}
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save Entry'}
                </Button>
              </div>
            </div>
          )}

          {!isReadOnly && !edit && entries.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="border-dashed border-mozaic-teal text-mozaic-teal"
              onClick={startAdd}
            >
              <Plus className="mr-1 h-3.5 w-3.5" /> Add Entry
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
